/**
 * Global Drawer Stack Types
 *
 * Type definitions specific to the drawer stack system.
 */

import { useDrawersStack } from '@mantine/core';
import { GLOBAL_MODAL_IDS } from '../../global-modal-stack/constants';

// Global drawer IDs registry — includes the modal stack IDs so that
// ResponsiveModal can open as a drawer on mobile (useResponsiveModalLayout
// calls drawerStack.openDrawer(globalModalId, …); Mantine silently ignores
// ids that were not registered at stack initialisation time).
const BASE_DRAWER_IDS: string[] = [

  // Mobile Detail Drawers
  'mobile-contact-detail', 'mobile-ticket-detail', 'mobile-task-detail',
  'mobile-time-entry-detail', 'mobile-company-detail', 'mobile-message-detail',
  'mobile-contact-details', 'mobile-google-drive-preview',
  
  // Mobile Action Drawers
  'mobile-contact-actions', 'mobile-ticket-actions', 'mobile-task-actions',
  'mobile-time-entry-actions', 'mobile-company-actions', 'mobile-message-actions',
  'mobile-task-actions-dropdown', 'mobile-task-action-due-date', 'mobile-ticket-actions-dropdown',
  'mobile-user-assignment', 'mobile-quickbooks-actions', 'mobile-quickbooks-details',
  'mobile-task-bulk-actions', 'mobile-task-due-date',
  
  // Mobile Navigation Drawers
  'mobile-navigation', 'mobile-search', 'mobile-filters',
  'mobile-saved-views', 'mobile-export-options', 'mobile-saved-views-management',
  'mobile-save-view-as', 'mobile-delete-view', 'mobile-share-view',
  
  // Mobile Form Drawers
  'mobile-contact-form', 'mobile-ticket-form', 'mobile-task-form',
  'mobile-time-entry-form', 'mobile-company-form', 'mobile-contact-import',
  'mobile-domain-search', 'mobile-form-sections',
  
  // Mobile Create Form Drawers
  'create-contacts-drawer', 'create-tickets-drawer', 'create-tasks-drawer',
  'create-time-entries-drawer', 'create-companies-drawer', 'create-messages-drawer',
  
  // Mobile Settings Drawers
  'mobile-account-settings', 'mobile-preferences',
  
  // Mobile CRM Drawers
  'mobile-compose-message', 'mobile-log-activity', 'mobile-bulk-email',
  'mobile-share-document', 'mobile-share-signature', 'mobile-create-meeting',
  'mobile-template-selector', 'mobile-template-dropdown', 'mobile-variable-picker',
  'mobile-preview-toggle',
  
  // Mobile Quick Actions
  'mobile-quick-ticket', 'mobile-quick-time-entry',
  
  // Mobile File Management
  'mobile-file-picker', 'mobile-google-drive',
  
  // Mobile Reports
  'mobile-report-actions', 'mobile-report-filters', 'mobile-reports-dropdown',
  
  // Mobile User Management
  'mobile-user-actions', 'mobile-user-form',
  
  // Mobile Calendar/Time Management
  'mobile-calendar-actions', 'mobile-person-selector',
  
  // Mobile Data Table Drawers
  'mobile-data-table-drawer', 'mobile-standardized-drawer', 'mobile-detail-modal',
  
  // Responsive Component Drawers
  'responsive-modal-drawer', 'responsive-popover-drawer', 'responsive-command-select',
  'mobile-select-drawer', 'mobile-command-multi-select',
];

// Merge base drawer IDs with all modal IDs so that ResponsiveModal can open
// as a Mantine drawer on mobile without being silently ignored.
export const GLOBAL_DRAWER_IDS: string[] = [...new Set([...BASE_DRAWER_IDS, ...GLOBAL_MODAL_IDS])];

export type GlobalDrawerId = string;

/**
 * Drawer configuration interface
 */
export interface DrawerConfig {
  id: string;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  height?: string;
  zIndex?: number;
  enableSwipeGesture?: boolean;
  showCloseButton?: boolean;
  className?: string;
}

/**
 * Default drawer configuration
 */
export const DEFAULT_DRAWER_CONFIG: Partial<DrawerConfig> = {
  side: "bottom",
  enableSwipeGesture: true,
  showCloseButton: true,
  // No default height: bottom/top sheets fit their content (capped at 85dvh in
  // CentralizedDrawer). Callers pass an explicit height to opt into a fixed size.
};

/**
 * Global Drawer Stack Context Type
 */
export interface GlobalDrawerStackContextType {
  drawerStack: ReturnType<typeof useDrawersStack<string>>;
  openDrawer: (id: GlobalDrawerId, config?: Partial<DrawerConfig>) => void;
  closeDrawer: (id: GlobalDrawerId) => void;
  closeAllDrawers: () => void;
  isDrawerOpen: (id: GlobalDrawerId) => boolean;
  getDrawerConfig: (id: GlobalDrawerId) => DrawerConfig | null;
  setDrawerConfig: (id: GlobalDrawerId, config: Partial<DrawerConfig>) => void;
}
